import { inject, Injectable } from '@angular/core';
import { DependencyActionEnum, LorenzControlTypeEnum, LorenzFormControl, LorenzFormElement, LorenzFormGroup, LorenzFormList, PermissionEnum, ProcessStatusEnum, salutationsConstants, validNumberValidator, yousignUnterschriftBisValidator } from '@lib/forms';
import { Validators } from '@angular/forms';
import { FormEmployeeService } from './request-form-employee';
import { RequestOptions } from '@lib/request';
import { AppEnvironmentEnum, AppIdEnum, formatBoolean, formatCurrency, formatDate, formatHours, formatLanguageCode, lineSeparatedList } from '@lib/shared';

@Injectable({
	providedIn: 'root'
})
export class RequestFormService {
	private readonly formEmployeeService = inject(FormEmployeeService);

	requestForm(options: RequestOptions, functions: { [key: string]: Function }) {
		// console.log('RequestFormService START', options.dialogData);

		const mainForm = new LorenzFormGroup(
			{
				_lorenz: this.groupLorenz(options, functions),
				veranstaltung: this.groupVeranstaltung(options, functions),
				aussteller: this.groupAussteller(options, functions),
				ansprechpartner: this.groupAnsprechpartner(options, functions),
				personal: this.groupPersonal(options, functions),
				sonstiges: this.groupSonstiges(options, functions)
			},
			null,
			null,
			{
				saveButtonFn: functions['saveButtonFn'],
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);

		return mainForm;
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupLorenz(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		const requiredLorenzMitarbeiter = options.appId === AppIdEnum.REQUEST ? [] : [Validators.required];
		const requiredLorenzEmail = options.appId === AppIdEnum.REQUEST ? [] : [Validators.required, Validators.email];
		const requiredUnterschriftBis = options.appId === AppIdEnum.REQUEST ? [] : [Validators.required, yousignUnterschriftBisValidator()];

		return new LorenzFormGroup(
			{
				lorenz_mitarbeiter: new LorenzFormControl('', requiredLorenzMitarbeiter, null, {
					order: 1,
					label: 'LorenzMesse Mitarbeiter/in',
					type: LorenzControlTypeEnum.SELECT,
					options: options.dialogData['lorenz_employees'],
					allowOnlyOptions: true,
					permissions: {
						backend_request_edit: PermissionEnum.FULL
					}
				}),

				lorenz_email: new LorenzFormControl('', requiredLorenzEmail, null, {
					order: 2,
					label: 'E-Mail-Empfänger bei Lorenz',
					type: LorenzControlTypeEnum.TEXT,
					permissions: {
						backend_request_edit: PermissionEnum.FULL
					},
					dependencies: [
						{
							controlName: '.lorenz_mitarbeiter',
							expectedValue: (value: any) => !!value,
							action: DependencyActionEnum.CODE,
							actionFunction: functions['setLorenzMailAddress']
						}
					]
				}),

				status: new LorenzFormControl<ProcessStatusEnum>(ProcessStatusEnum.NEW, [], null, {
					order: 3,
					label: 'Bearbeitungsstatus',
					type: LorenzControlTypeEnum.OBJECT
				}),

				unterschrift_bis: new LorenzFormControl(null, requiredUnterschriftBis, null, {
					order: 3,
					label: 'Unterschrift bis',
					type: LorenzControlTypeEnum.DATE,
					permissions: {
						backend_request_edit: PermissionEnum.FULL
					}
				}),

				letzte_unterschriftsaufforderung: new LorenzFormControl(null, [], null, {
					order: 4,
					label: 'Letzte Unterschriftsaufforderung',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => formatDate(value, '-')
				}),

				// rueckruf: new LorenzFormControl(null, [], null, {
				// 	order: 5,
				// 	label: 'Rückruf gewünscht',
				// 	type: LorenzControlTypeEnum.OBJECT,
				// 	displayFunction: (value: boolean) => formatBoolean(value)
				// }),

				sprache: new LorenzFormControl(null, [], null, {
					order: 6,
					label: 'Anfragesprache',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => formatLanguageCode(value)
				}),

				datum: new LorenzFormControl(null, [], null, {
					order: 7,
					label: 'Anfragedatum',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => formatDate(value)
				}),

				uhrzeit: new LorenzFormControl(null, [], null, {
					order: 8,
					label: 'Anfrageuhrzeit',
					type: LorenzControlTypeEnum.OBJECT
				}),

				json_data_version: new LorenzFormControl(null, [], null, {
					order: 999,
					label: 'JSON Data Version',
					type: LorenzControlTypeEnum.OBJECT,
					permissions: {
						backend_request_edit: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
					}
				}),

				last_update: new LorenzFormControl(null, [], null, {
					order: 999,
					label: 'Letzte Aktualisierung',
					type: LorenzControlTypeEnum.OBJECT,
					permissions: {
						backend_request_edit: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
					}
				}),

				yousign: this.yousign(options, functions),

				archiviert: new LorenzFormControl<boolean>(false, [], null, {
					order: 9999,
					label: 'archiviert	',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: boolean) => formatBoolean(value),
					permissions: {
						backend_request_edit: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN,
						request: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
					}
				}),

				geloescht: new LorenzFormControl<boolean>(false, [], null, {
					order: 9999,
					label: 'gelöscht',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: boolean) => formatBoolean(value),
					permissions: {
						backend_request_edit: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN,
						request: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
					}
				})
			},
			null,
			null,
			{
				order: 'A',
				hasSaveButton: false,
				permissions: {
					backend_request_edit: PermissionEnum.READ,
					request: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupVeranstaltung(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				veranstaltungsname: new LorenzFormControl('', [Validators.required], null, {
					order: 1,
					label: 'Veranstaltungsname',
					type: LorenzControlTypeEnum.TEXT
				}),

				hallennummer: new LorenzFormControl('', [Validators.required], null, {
					order: 2,
					label: 'Hallennummer',
					type: LorenzControlTypeEnum.TEXT
				}),

				standnummer: new LorenzFormControl('', [Validators.required], null, {
					order: 3,
					label: 'Standnummer',
					type: LorenzControlTypeEnum.TEXT
				})
			},
			null,
			null,
			{
				order: 'B',
				label: 'Veranstaltungsinformationen',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupAussteller(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				...this.groupDetailsAussteller(options, functions),

				kundennummer: new LorenzFormControl('', [], null, {
					order: 10,
					label: 'Kundennummer',
					type: LorenzControlTypeEnum.TEXT,
					permissions: {
						request: PermissionEnum.HIDDEN
					}
				}),

				betriebsnummer: new LorenzFormControl('', [], null, {
					order: 10,
					label: 'Betriebsnummer',
					type: LorenzControlTypeEnum.TEXT,
					permissions: {
						request: PermissionEnum.HIDDEN
					}
				}),

				abweichende_rechnungsanschrift: new LorenzFormControl('', [], null, {
					order: 'B',
					label: 'Abweichende Rechnungsanschrift',
					type: LorenzControlTypeEnum.CHECKBOX
				}),

				rechnungsanschrift: new LorenzFormGroup(
					{
						...this.groupDetailsAussteller(options, functions)
					},
					null,
					null,
					{
						order: 1,
						label: 'Rechnungsanschrift',
						hasSaveButton: false,
						dependencies: [
							{
								controlName: '.abweichende_rechnungsanschrift',
								expectedValue: (value: any) => !!value,
								action: DependencyActionEnum.ENABLE
							}
						]
					}
				)
			},
			null,
			null,
			{
				order: 'C',
				label: 'Ausstellerinformationen',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupAnsprechpartner(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				...this.groupDetailsAnsprechpartner(functions),

				ansprechpartner_vor_ort_vorhanden: new LorenzFormControl('', [], null, {
					order: 'A',
					label: 'Ansprechpartner vor Ort',
					type: LorenzControlTypeEnum.CHECKBOX
				}),

				ansprechpartner_vor_ort: new LorenzFormGroup(
					{
						...this.groupDetailsAnsprechpartner(functions)
					},
					null,
					null,
					{
						order: 'A',
						label: 'Ansprechpartner vor Ort',
						hasSaveButton: false,
						dependencies: [
							{
								controlName: '.ansprechpartner_vor_ort_vorhanden',
								expectedValue: (value: any) => !!value,
								action: DependencyActionEnum.ENABLE
							}
						]
					}
				)
			},
			null,
			null,
			{
				order: 'D',
				label: 'Ansprechpartner für Rückfragen',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupPersonal(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				positionen: new LorenzFormList([], [Validators.required, validNumberValidator], null, {
					order: 1,
					type: LorenzControlTypeEnum.LIST,
					hasAddButton: true,
					addButtonText: 'Weitere Position',
					hasDeleteButton: true,
					valueChanges: functions['isKundennummerRequired'],
					listItemForm: this.formEmployeeService.formEmployee,
					listItemFormPayload: {
						options,
						functions
					},
					listItemRemoveFn: functions['calculatePersonalPositions']
				}),

				ignore_minimum_booking_period: new LorenzFormControl(false, [], null, {
					order: '1.5',
					label: 'Mindestbuchungsdauer ignorieren (unter 5h erlauben)',
					type: LorenzControlTypeEnum.CHECKBOX,
					valueChanges: functions['calculatePersonalPositions'],
					permissions: {
						backend_request_edit: PermissionEnum.FULL,
						request: PermissionEnum.HIDDEN
					}
				}),

				ignore_last_minute_surcharge: new LorenzFormControl(false, [], null, {
					order: '1.6',
					label: 'Last Minute Zuschlag ignorieren (unter 14 Tagen ohne Zuschlag)',
					type: LorenzControlTypeEnum.CHECKBOX,
					valueChanges: functions['calculatePersonalPositions'],
					permissions: {
						backend_request_edit: PermissionEnum.FULL,
						request: PermissionEnum.HIDDEN
					}
				}),

				summen: new LorenzFormGroup(
					{
						last_minute_zuschlag: new LorenzFormControl(false, [], null, {
							order: 4,
							label: 'Last Minute Zuschlag',
							type: LorenzControlTypeEnum.OBJECT,
							disableValueBuffer: true,
							displayFunction: (value: boolean): string => formatBoolean(value),
							dependencies: [
								{
									controlName: '.last_minute_text',
									expectedValue: (value: any) => !!value,
									action: DependencyActionEnum.ENABLE
								}
							],
							permissions: {
								request: PermissionEnum.HIDDEN
							}
						}),

						last_minute_text: new LorenzFormControl('', [], null, {
							order: 1,
							type: LorenzControlTypeEnum.HTML,
							disableValueBuffer: true,
							valueChanges: functions['setLastMinuteHintFn'],
							dependencies: [
								{
									controlName: '.last_minute_text',
									expectedValue: (value: any) => !!value,
									action: DependencyActionEnum.ENABLE
								}
							]
						}),

						stunden: new LorenzFormControl('', [], null, {
							order: 2,
							label: 'Summe Stunden',
							type: LorenzControlTypeEnum.OBJECT,
							displayFunction: (value: string): string => (value ? formatHours(value) : '-')
						}),

						preise: new LorenzFormControl('', [], null, {
							order: 3,
							label: 'Summe Preise',
							type: LorenzControlTypeEnum.OBJECT,
							displayFunction: (value: string) => (value ? formatCurrency(value) : '-')
						})
					},
					null,
					null,
					{
						order: '2',
						label: 'Summen aller Positionen',
						permissions: {
							backend_request_edit: PermissionEnum.READ,
							request: PermissionEnum.READ
						}
					}
				),

				// addButton: new LorenzFormControl('', [], null, {
				// 	order: 3,
				// 	type: LorenzControlTypeEnum.HTML,
				// 	htmlTemplate: options.addButtonTemplate
				// }),

				dresscode: new LorenzFormControl('', [], null, {
					order: 4,
					label: 'Dresscode',
					placeholder: 'Unsere Mitarbeiter erscheinen im Dresscode "Business Look / schwarz-weiß". Ist ein anderer Dresscode gewünscht, machen Sie hier bitte entsprechende Angaben.',
					type: LorenzControlTypeEnum.TEXTAREA
				}),

				kleidung_wird_gestellt: new LorenzFormControl('', [], null, {
					order: 5,
					label: 'Kleidung wird gestellt',
					type: LorenzControlTypeEnum.CHECKBOX
				}),

				kleidung: new LorenzFormControl('', [Validators.required], null, {
					order: 6,
					label: 'Bereitgestellte Kleidung',
					type: LorenzControlTypeEnum.TEXTAREA,
					placeholder: 'Bitte geben Sie hier die Art der bereitgestellten Kleidung z.B. Hemd, T-Shirt, Bluse, Hose, usw., an.',
					dependencies: [
						{
							controlName: '.kleidung_wird_gestellt',
							expectedValue: (value: any) => !!value,
							action: DependencyActionEnum.ENABLE
						}
					]
				})
			},
			null,
			null,
			{
				order: 'E',
				label: 'Benötigtes Personal',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	groupSonstiges(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				text: new LorenzFormControl('', [], null, {
					order: 1,
					type: LorenzControlTypeEnum.TEXTAREA
				})
			},
			null,
			null,
			{
				order: 'F',
				label: 'Sonstiges',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend_request_edit: PermissionEnum.FULL,
					request: PermissionEnum.HIDDEN
				}
			}
		);
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	private groupDetailsAussteller(options: RequestOptions, functions: { [key: string]: Function }) {
		return {
			firmenname: new LorenzFormControl('', [Validators.required], null, {
				order: 1,
				label: 'Firmenname',
				type: LorenzControlTypeEnum.TEXT
			}),

			strasse: new LorenzFormControl('', [Validators.required], null, {
				order: 2,
				label: 'Straße',
				type: LorenzControlTypeEnum.TEXT
			}),

			hausnummer: new LorenzFormControl('', [Validators.required], null, {
				order: 3,
				label: 'Hausnummer',
				type: LorenzControlTypeEnum.TEXT
			}),

			plz: new LorenzFormControl('', [Validators.required], null, {
				order: 4,
				label: 'PLZ',
				type: LorenzControlTypeEnum.TEXT
			}),

			ort: new LorenzFormControl('', [Validators.required], null, {
				order: 5,
				label: 'Ort',
				type: LorenzControlTypeEnum.TEXT
			}),

			land: new LorenzFormControl('', [Validators.required], null, {
				order: 6,
				label: 'Land',
				type: LorenzControlTypeEnum.SELECT,
				options: options.dialogData?.countries,
				defaultOption: 'DE',
				valueChanges: functions['isVatRequired']
			}),

			ustId: new LorenzFormControl('', [], null, {
				order: 7,
				label: 'USt-Id / VAT',
				type: LorenzControlTypeEnum.TEXT
			})
		};
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	private groupDetailsAnsprechpartner(functions: { [key: string]: Function }) {
		return {
			anrede: new LorenzFormControl('', [Validators.required], null, {
				order: 1,
				label: 'Anrede',
				type: LorenzControlTypeEnum.SELECT,
				options: salutationsConstants
			}),

			nachname: new LorenzFormControl('', [Validators.required], null, {
				order: 2,
				label: 'Nachname',
				type: LorenzControlTypeEnum.TEXT
			}),

			vorname: new LorenzFormControl('', [Validators.required], null, {
				order: 3,
				label: 'Vorname',
				type: LorenzControlTypeEnum.TEXT
			}),

			email: new LorenzFormControl('', [Validators.required, Validators.email], null, {
				order: 4,
				label: 'E-Mail',
				type: LorenzControlTypeEnum.TEXT
			}),

			telefon: new LorenzFormControl('', [Validators.required], null, {
				order: 5,
				label: 'Telefon',
				type: LorenzControlTypeEnum.TEXT
			})
		};
	}

	// ---------------------------------------------------------------------------------------------------------------- //

	yousign(options: RequestOptions, functions: { [key: string]: Function }): LorenzFormElement {
		return new LorenzFormGroup(
			{
				signature_request_id: new LorenzFormControl(null, [], null, {
					order: 1,
					label: 'Yousign Signature Request ID',
					type: LorenzControlTypeEnum.OBJECT
				}),

				documents_ids: new LorenzFormControl(null, [], null, {
					order: 2,
					label: 'Yousign Documents IDs',
					type: LorenzControlTypeEnum.HTML,
					innerHTML: (items: any): string => lineSeparatedList(items)
				}),

				signers_ids: new LorenzFormControl(null, [], null, {
					order: 3,
					label: 'Yousign Signers IDs',
					type: LorenzControlTypeEnum.HTML,
					innerHTML: (items: any): string => lineSeparatedList(items)
				}),

				deny_reason: new LorenzFormControl(null, [], null, {
					order: 4,
					label: 'Unterschriftsverweigerung Grund',
					type: LorenzControlTypeEnum.OBJECT,
					dependencies: [
						{
							controlName: '_lorenz.status',
							expectedValue: (value: ProcessStatusEnum) => !!value && [ProcessStatusEnum.CANCELED, ProcessStatusEnum.DECLINED].includes(value),
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				deny_datetime: new LorenzFormControl(null, [], null, {
					order: 5,
					label: 'Unterschriftsverweigerung Zeitpunkt',
					type: LorenzControlTypeEnum.OBJECT,
					dependencies: [
						{
							controlName: '_lorenz.status',
							expectedValue: (value: ProcessStatusEnum) => !!value && [ProcessStatusEnum.CANCELED, ProcessStatusEnum.DECLINED].includes(value),
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				done_datetime: new LorenzFormControl(null, [], null, {
					order: 6,
					label: 'Yousign abgeschlossen am',
					type: LorenzControlTypeEnum.OBJECT,
					dependencies: [
						{
							controlName: '_lorenz.status',
							expectedValue: (value: ProcessStatusEnum) => !!value && value === ProcessStatusEnum.SIGNED,
							action: DependencyActionEnum.ENABLE
						}
					]
				})
			},
			null,
			null,
			{
				order: '999',
				hasSaveButton: false,
				permissions: {
					backend_contract_edit: [AppEnvironmentEnum.LOCAL, AppEnvironmentEnum.DEV].includes(options.appEnvironment) ? PermissionEnum.READ : PermissionEnum.HIDDEN
				}
			}
		);
	}
}
